import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Gift, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const MyBookieExitPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleMouseLeave = useCallback((e) => {
    // Only trigger when mouse leaves from the top of the viewport
    if (e.clientY <= 0) {
      // Don't show for logged-in users
      const token = sessionStorage.getItem('mybookie_token');
      if (token) return;

      const dismissed = sessionStorage.getItem('mybookie_exit_popup_dismissed');
      const submitted = sessionStorage.getItem('mybookie_exit_popup_submitted');
      
      if (!dismissed && !submitted) {
        setIsVisible(true);
      }
    }
  }, []);

  useEffect(() => {
    // Don't show on mobile (no mouse leave detection)
    if (window.innerWidth < 768) return;
    // Don't show for logged-in users
    if (sessionStorage.getItem('mybookie_token')) return;
    
    // Add delay before enabling exit intent
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000); // 5 second delay

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem('mybookie_exit_popup_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Save email to backend
      await axios.post(`${API}/api/mybookie/subscribe`, { email });
      
      sessionStorage.setItem('mybookie_exit_popup_submitted', 'true');
      setSubmitted(true);
      
      // Auto close after 3 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 3000);
    } catch (err) {
      // If endpoint doesn't exist, just save locally and show success
      sessionStorage.setItem('mybookie_exit_popup_submitted', 'true');
      setSubmitted(true);
      
      setTimeout(() => {
        setIsVisible(false);
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-sm shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#8A948F] hover:text-[#111614] transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with accent */}
        <div className="bg-[#0A2B1D] p-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-sm flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">
            Wait! Don't leave empty-handed
          </h2>
          <p className="text-[#B4C9BE]">
            Get our free guide to tax deductions for entrepreneurs
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {!submitted ? (
            <>
              <div className="mb-6">
                <h3 className="font-medium text-[#111614] mb-2">What you'll get:</h3>
                <ul className="space-y-2 text-sm text-[#5C6661]">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-[#E2ECE7] rounded-sm flex items-center justify-center text-[#0A2B1D] text-xs">✓</span>
                    50+ tax deductions most entrepreneurs miss
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-[#E2ECE7] rounded-sm flex items-center justify-center text-[#0A2B1D] text-xs">✓</span>
                    Mileage tracking cheat sheet
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-[#E2ECE7] rounded-sm flex items-center justify-center text-[#0A2B1D] text-xs">✓</span>
                    Home office deduction calculator
                  </li>
                </ul>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-[#FAFAF8] border-[#E6E8E6]"
                    data-testid="exit-popup-email"
                  />
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm px-6"
                  >
                    {loading ? '...' : <ArrowRight className="w-5 h-5" />}
                  </Button>
                </div>
                {error && (
                  <p className="text-red-500 text-sm mt-2">{error}</p>
                )}
              </form>

              <p className="text-xs text-[#8A948F] text-center mt-4">
                No spam. Unsubscribe anytime.
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-[#E2ECE7] rounded-sm flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#0A2B1D]" />
              </div>
              <h3 className="text-xl font-medium text-[#111614] mb-2">You're in!</h3>
              <p className="text-[#5C6661]">
                Check your inbox for the free guide.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-[#F6F5F2] px-6 py-3 text-center">
          <button 
            onClick={handleClose}
            className="text-sm text-[#8A948F] hover:text-[#5C6661] transition-colors"
          >
            No thanks, I'll pass on free tax savings
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyBookieExitPopup;
